import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CompanyCard } from "@/components/companies/CompanyCard";
import { CompanyDetail } from "@/components/companies/CompanyDetail";
import { ContactProfile } from "@/components/contacts/ContactProfile";
import { CreateCompanyModal } from "@/components/companies/CreateCompanyModal";
import { EditCompanyModal } from "@/components/companies/EditCompanyModal";
import {
  companyAPI,
  Company,
  CreateCompanyData,
  UpdateCompanyData,
} from "@/services/companyService";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import {
  Plus,
  Search,
  Building2,
  Factory,
  Globe,
  Calendar,
  Users,
} from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import { IndustriesManagement } from "@/components/industries/IndustriesManagement";
import { CardGridSkeleton } from "@/components/shared/CardGridSkeleton";

export default function Companies({ hideHeader = false }: { hideHeader?: boolean }) {
  const { toast } = useToast();
  const { company, isAuthenticated } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedContact, setSelectedContact] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);

  const getCompanySlug = (): string | null => {
    if (company?.slug) {
      return company.slug;
    }

    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsedCompany = JSON.parse(storedCompany);
        if (parsedCompany?.slug) {
          return parsedCompany.slug;
        }
      }
    } catch (error) {}

    try {
      const sessionCompany = sessionStorage.getItem("company");
      if (sessionCompany) {
        const parsedCompany = JSON.parse(sessionCompany);
        if (parsedCompany?.slug) {
          return parsedCompany.slug;
        }
      }
    } catch (error) {}

    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser?.company?.slug) {
          return parsedUser.company.slug;
        }
      }
    } catch (error) {}

    return null;
  };

  const companySlug = getCompanySlug();

  useEffect(() => {
    if (isAuthenticated && companySlug) {
      fetchCompanies();
    } else if (!isAuthenticated) {
      setLoading(false);
      toast({
        title: "Acceso requerido",
        description: "Debes iniciar sesión para ver las empresas",
        variant: "destructive",
      });
    } else if (!companySlug) {
      setLoading(false);
      toast({
        title: "Error de configuración",
        description: "No se pudo identificar la organización",
        variant: "destructive",
      });
    }
  }, [isAuthenticated, companySlug]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response: any = await companyAPI.getCompanies({
        slug: companySlug!,
        page: 1,
        limit: 50,
      });

      // Manejar respuesta tanto si es array directo como si es objeto con propiedad data
      const companiesList = Array.isArray(response)
        ? response
        : Array.isArray(response.data)
          ? response.data
          : [];

      setCompanies(companiesList);

      if (companiesList.length === 0) {
        toast({
          title: "Sin empresas",
          description: "No hay empresas registradas. Crea la primera.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al cargar empresas",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getIndustryName = (company: Company): string => {
    if (!company) return "";

    if (typeof company.industry === "string") {
      return company.industry;
    }

    if (company.industry && typeof company.industry === "object") {
      const industry = company.industry as any;
      return industry.name || company.industry_id || "";
    }

    return company.industry_id || "";
  };

  const totalCompanies = companies.length;
  const companiesWithIndustry = companies.filter((c) =>
    getIndustryName(c),
  ).length;
  const companiesWithWebsite = companies.filter((c) => c.website).length;

  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const currentMonthName = new Date().toLocaleString("en-US", {
    month: "long",
  });
  const newCompaniesThisMonth = companies.filter((c) => {
    const createdAt = (c as any).createdAt || (c as any).created_at;
    if (!createdAt) return false;
    const date = new Date(createdAt);
    return (
      date.getMonth() === currentMonth && date.getFullYear() === currentYear
    );
  }).length;

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const filteredCompanies = useMemo(
    () =>
      companies.filter(
        (company) =>
          company.name
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          getIndustryName(company)
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()),
      ),
    [companies, debouncedSearchQuery],
  );

  const handleCompanyClick = (company: Company) => {
    setSelectedCompany(company);
  };

  const handleContactClick = (contact: any) => {
    setSelectedContact(contact);
    setSelectedCompany(null);
  };

  const handleSaveCompany = async (companyData: CreateCompanyData, rtuFile: File | null = null) => {
    if (!companySlug) {
      toast({
        title: "Error",
        description: "No se encontró el slug de la organización",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaveLoading(true);
      const response = await companyAPI.createCompany({
        ...companyData,
        slug: companySlug,
      });

      let targetCompanyId = response.data.id;

      // Si hay archivo RTU, subir a S3
      if (rtuFile && targetCompanyId) {
        toast({
          title: "Subiendo documento...",
          description: "Por favor espera mientras se sube el archivo RTU.",
        });

        const uploadUrlResponse = await companyAPI.getUploadUrl(
          companySlug,
          targetCompanyId,
          rtuFile.name,
          rtuFile.type || "application/octet-stream",
        );

        if (uploadUrlResponse.success && uploadUrlResponse.data) {
          const urlData = uploadUrlResponse.data;
          
          if (urlData?.upload_url && urlData?.file_url) {
            await companyAPI.uploadToS3(urlData.upload_url, rtuFile);
            
            await companyAPI.updateCompany(targetCompanyId, {
              slug: companySlug,
              rtu_url: urlData.file_url,
            });
            
            response.data.rtu_url = urlData.file_url;
          }
        }
      }

      setCompanies((prev) => [...prev, response.data]);
      setIsCreateModalOpen(false);

      toast({
        title: "Empresa creada",
        description: `La empresa "${companyData.name}" ha sido creada exitosamente.`,
      });
      await fetchCompanies();
    } catch (error: any) {
      console.error("Error al crear empresa:", error);

      toast({
        title: "Error",
        description: error.message || "Error al crear empresa",
        variant: "destructive",
      });

      if (error.message && error.message.includes("creó")) {
        console.log(
          "Recargando lista de empresas por si se creó correctamente...",
        );
        await fetchCompanies();
        setIsCreateModalOpen(false);
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdateCompany = async (id: string, data: UpdateCompanyData, rtuFile: File | null = null) => {
    if (!companySlug) {
      toast({
        title: "Error",
        description: "No se encontró el slug de la organización",
        variant: "destructive",
      });
      return;
    }

    try {
      const updateData = {
        ...data,
        slug: companySlug,
      };
      const response = await companyAPI.updateCompany(id, updateData);

      if (rtuFile) {
        toast({
          title: "Subiendo documento...",
          description: "Actualizando el archivo RTU.",
        });

        const uploadUrlResponse = await companyAPI.getUploadUrl(
          companySlug,
          id,
          rtuFile.name,
          rtuFile.type || "application/octet-stream",
        );

        if (uploadUrlResponse.success && uploadUrlResponse.data) {
          const urlData = uploadUrlResponse.data;
          
          if (urlData?.upload_url && urlData?.file_url) {
            await companyAPI.uploadToS3(urlData.upload_url, rtuFile);
            
            await companyAPI.updateCompany(id, {
              slug: companySlug,
              rtu_url: urlData.file_url,
            });
            
            response.data.rtu_url = urlData.file_url;
          }
        }
      }

      setCompanies(
        companies.map((company) =>
          company.id === id ? response.data : company,
        ),
      );

      if (selectedCompany?.id === id) {
        setSelectedCompany(response.data);
      }

      setIsEditModalOpen(false);
      setEditingCompany(null);

      toast({
        title: "Empresa actualizada",
        description: "Empresa actualizada correctamente",
      });
    } catch (error: any) {
      toast({
        title: "❌ Error",
        description: error.message || "Error al actualizar empresa",
        variant: "destructive",
      });
    }
  };

  const handleEditClick = (id: string, companyData: Partial<Company>) => {
    if (!selectedCompany) return;

    setEditingCompany(selectedCompany);
    setIsEditModalOpen(true);
  };

  const handleDeleteCompany = async (id: string) => {
    if (!companySlug) return;

    try {
      await companyAPI.deleteCompany(id, companySlug);
      setCompanies(companies.filter((company) => company.id !== id));
      setSelectedCompany(null);
      toast({
        title: "Empresa eliminada",
        description: "Empresa eliminada correctamente",
      });
    } catch (error: any) {
      toast({
        title: "❌ Error",
        description: error.message || "Error al eliminar empresa",
        variant: "destructive",
      });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">
            Acceso no autorizado
          </h1>
          <p className="text-slate-600">
            Debes iniciar sesión para ver esta página
          </p>
        </div>
      </div>
    );
  }

  if (!companySlug) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">
            Error de configuración
          </h1>
          <p className="text-slate-600 mb-4">
            No se pudo identificar tu organización. Por favor, contacta al
            administrador.
          </p>
          <Button onClick={() => window.location.reload()}>Reintentar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className={`flex items-center ${hideHeader ? "justify-end" : "justify-between"}`}>
        {!hideHeader && (
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Empresas Corporativas</h1>
            <p className="text-slate-600">Gestiona las entidades comerciales de tus clientes</p>
          </div>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setIsIndustriesModalOpen(true)}
          >
            <Factory className="h-4 w-4 mr-2" />
            Industrias
          </Button>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-black hover:bg-gray-800 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nueva Empresa
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600">Total Empresas</p>
                <p className="text-2xl font-bold text-slate-900">
                  {totalCompanies}
                </p>
              </div>
              <Building2 className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600">Con Industria</p>
                <p className="text-2xl font-bold text-purple-600">
                  {companiesWithIndustry}
                </p>
              </div>
              <Factory className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600">Con Sitio Web</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {companiesWithWebsite}
                </p>
              </div>
              <Globe className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600">
                  Nuevas este Mes{" "}
                  <span className="capitalize text-slate-400 font-medium">
                    ({currentMonthName})
                  </span>
                </p>
                <p className="text-2xl font-bold text-orange-600">
                  {newCompaniesThisMonth}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Buscar empresas por nombre o industria..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {loading ? (
        <CardGridSkeleton hasMetrics={false} />
      ) : filteredCompanies.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
          <div className="mx-auto w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
            <Building2 className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            No hay empresas registradas
          </h3>
          <p className="text-slate-600 mb-6">
            Comienza creando tu primera empresa cliente
          </p>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Crear primera empresa
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompanies.map((company) => (
              <CompanyCard
                key={company.id}
                company={company}
                onClick={handleCompanyClick}
              />
            ))}
          </div>

          {filteredCompanies.length > 0 && (
            <div className="text-sm text-slate-500 text-center">
              Mostrando {filteredCompanies.length} de {companies.length}{" "}
              empresas
            </div>
          )}
        </>
      )}

      <CompanyDetail
        company={selectedCompany}
        isOpen={!!selectedCompany}
        onClose={() => setSelectedCompany(null)}
        onContactClick={handleContactClick}
        onUpdate={handleEditClick}
        onDelete={handleDeleteCompany}
      />

      <ContactProfile
        contact={selectedContact}
        isOpen={!!selectedContact}
        onClose={() => setSelectedContact(null)}
      />

      <CreateCompanyModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleSaveCompany}
        companySlug={companySlug!}
      />

      <EditCompanyModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingCompany(null);
        }}
        onSave={handleUpdateCompany}
        company={editingCompany}
        companySlug={companySlug!}
      />

      <Dialog
        open={isIndustriesModalOpen}
        onOpenChange={setIsIndustriesModalOpen}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gestión de Industrias</DialogTitle>
          </DialogHeader>
          <IndustriesManagement />
        </DialogContent>
      </Dialog>
    </div>
  );
}

